// components/ProductSelection/EnhancedProductCard.jsx
import React, { useState } from "react";
import Badge from "../../common/Badge";
import ProductDetailsModal from "./ProductDetailsModal";
import {
  CubeIcon,
  CheckCircleIcon,
  ShieldCheckIcon,
  PencilIcon,
  EyeIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  SparklesIcon,
  ScaleIcon,
  TagIcon,
  PhotographIcon,
  VideoCameraIcon,
  PlayIcon,
} from "@heroicons/react/outline";

const EnhancedProductCard = ({
  product,
  viewMode = "grid",
  isSelected,
  onSelect,
  onEdit,
  showPricing = true,
  multiSelect = false,
  showCheckbox = true,
}) => {
  const [showPricingDetails, setShowPricingDetails] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const stockStatus = React.useMemo(() => {
    const stock = product.stock?.availableQuantity || 0;
    if (stock === 0) return { label: "Out of Stock", variant: "danger" };
    if (stock <= (product.stock?.minStockLevel || 1)) {
      return { label: "Low Stock", variant: "warning" };
    }
    return { label: "In Stock", variant: "success" };
  }, [product.stock]);

  const primaryPricing = React.useMemo(() => {
    if (!product.pricing?.branches) return null;
    const branches = Object.entries(product.pricing.branches);
    const availableBranch = branches.find(([_, data]) => data.available);
    return availableBranch ? availableBranch[1] : null;
  }, [product.pricing]);

  const handleCardClick = (e) => {
    // Don't trigger selection if clicking on action buttons
    if (
      e.target.closest(".action-button") ||
      e.target.closest(".pricing-toggle")
    ) {
      return;
    }
    // Open details modal
    setShowDetails(true);
  };

  // Count media types
  const imageCount =
    product.media?.filter((m) => m.type === "image").length || 0;
  const videoCount =
    product.media?.filter((m) => m.type === "video").length || 0;
  const firstMedia = product.media?.[0];
  const isFirstVideo = firstMedia?.type === "video";

  if (viewMode === "list") {
    return (
      <>
        <div
          className={`group relative p-4 border rounded-xl cursor-pointer transition-all duration-200 ${
            isSelected
              ? "border-primary bg-primary-50 shadow-md"
              : "border-gray-200 hover:border-primary hover:shadow-md bg-white"
          }`}
          onClick={handleCardClick}
        >
          <div className="flex gap-4">
            {/* Checkbox */}
            {showCheckbox && multiSelect && (
              <div
                className="flex-shrink-0 pt-1"
                onClick={(e) => {
                  e.stopPropagation();
                  onSelect();
                }}
              >
                <div
                  className={`w-5 h-5 border-2 rounded transition-all ${
                    isSelected
                      ? "bg-primary border-primary"
                      : "border-gray-300 group-hover:border-primary"
                  }`}
                >
                  {isSelected && (
                    <CheckCircleIcon className="w-full h-full text-white" />
                  )}
                </div>
              </div>
            )}

            {/* Product Media */}
            <div className="flex-shrink-0 w-24 h-24 bg-gray-100 rounded-lg overflow-hidden relative">
              {firstMedia ? (
                <>
                  {isFirstVideo ? (
                    <>
                      <video
                        src={firstMedia.url}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                        <PlayIcon className="w-8 h-8 text-white" />
                      </div>
                    </>
                  ) : (
                    <img
                      src={firstMedia.url}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  )}
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <CubeIcon className="w-10 h-10 text-gray-400" />
                </div>
              )}
            </div>

            {/* Product Details */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 text-lg truncate">
                    {product.name}
                  </h3>
                  <p className="text-sm text-gray-500 mt-0.5">
                    SKU: {product.sku} • ID: {product.productId}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {/* View Details Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowDetails(true);
                    }}
                    className="action-button p-2 text-gray-600 hover:text-primary hover:bg-primary-50 rounded-lg transition-colors"
                    title="View Details"
                  >
                    <EyeIcon className="w-5 h-5" />
                  </button>
                  {onEdit && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit();
                      }}
                      className="action-button p-2 text-gray-600 hover:text-primary hover:bg-primary-50 rounded-lg transition-colors"
                      title="Edit Product"
                    >
                      <PencilIcon className="w-5 h-5" />
                    </button>
                  )}
                </div>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Badge variant="secondary" size="sm">
                  <SparklesIcon className="w-3 h-3 mr-1" />
                  {product.type}
                </Badge>
                {product.subType && (
                  <Badge variant="secondary" size="sm">
                    {product.subType.replace("_", " ")}
                  </Badge>
                )}
                <Badge variant={stockStatus.variant} size="sm">
                  {stockStatus.label} ({product.stock?.availableQuantity || 0})
                </Badge>
                {product.hallmark?.isHallmarked && (
                  <Badge variant="warning" size="sm">
                    <ShieldCheckIcon className="w-3 h-3 mr-1" />
                    Hallmarked
                  </Badge>
                )}
                {product.isFeatured && (
                  <Badge variant="success" size="sm">
                    Featured
                  </Badge>
                )}
                {imageCount > 0 && (
                  <Badge variant="secondary" size="sm">
                    <PhotographIcon className="w-3 h-3 mr-1" />
                    {imageCount}
                  </Badge>
                )}
                {videoCount > 0 && (
                  <Badge variant="info" size="sm">
                    <VideoCameraIcon className="w-3 h-3 mr-1" />
                    {videoCount}
                  </Badge>
                )}
              </div>

              <div className="mt-3 grid grid-cols-4 gap-4">
                {/* Weights */}
                <div className="flex items-center gap-2">
                  <ScaleIcon className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-500">Gross Weight</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {product.grossWeight}g
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <ScaleIcon className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-500">Net Weight</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {product.netWeight}g
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <SparklesIcon className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-500">Purity</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {product.metalDetails?.purity || product.purity}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <TagIcon className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-500">Metal Color</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {product.metalDetails?.color || "Yellow"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Pricing Section */}
            {showPricing && primaryPricing && (
              <div className="flex-shrink-0 w-64 border-l border-gray-200 pl-4">
                <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-lg p-4">
                  <p className="text-xs text-primary-700 font-medium mb-1">
                    {Object.keys(product.pricing?.branches || {}).find(
                      (key) => product.pricing.branches[key] === primaryPricing
                    )}
                  </p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold text-primary">
                      ₹
                      {primaryPricing.breakdown?.finalAmount?.toLocaleString(
                        "en-IN",
                        { maximumFractionDigits: 0 }
                      )}
                    </span>
                  </div>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowPricingDetails(!showPricingDetails);
                    }}
                    className="pricing-toggle mt-3 text-xs text-primary-700 hover:text-primary-900 font-medium flex items-center gap-1"
                  >
                    {showPricingDetails ? "Hide" : "Show"} Breakdown
                    {showPricingDetails ? (
                      <ChevronUpIcon className="w-3 h-3" />
                    ) : (
                      <ChevronDownIcon className="w-3 h-3" />
                    )}
                  </button>

                  {/* Pricing Breakdown */}
                  {showPricingDetails && (
                    <div className="mt-3 pt-3 border-t border-primary-200 space-y-2 text-xs">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Metal Value:</span>
                        <span className="font-medium text-gray-900">
                          ₹
                          {primaryPricing.breakdown?.metalValue?.toLocaleString(
                            "en-IN",
                            { maximumFractionDigits: 0 }
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Making Charges:</span>
                        <span className="font-medium text-gray-900">
                          ₹
                          {primaryPricing.breakdown?.makingCharges?.toLocaleString(
                            "en-IN",
                            { maximumFractionDigits: 0 }
                          )}
                        </span>
                      </div>
                      {primaryPricing.breakdown?.wastageCharges > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Wastage:</span>
                          <span className="font-medium text-gray-900">
                            ₹
                            {primaryPricing.breakdown?.wastageCharges?.toLocaleString(
                              "en-IN",
                              { maximumFractionDigits: 0 }
                            )}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-gray-600">
                          GST ({primaryPricing.breakdown?.gst?.rate}%):
                        </span>
                        <span className="font-medium text-gray-900">
                          ₹
                          {primaryPricing.breakdown?.gst?.amount?.toLocaleString(
                            "en-IN",
                            { maximumFractionDigits: 0 }
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-primary-200">
                        <span className="font-semibold text-gray-900">
                          Total:
                        </span>
                        <span className="font-bold text-primary">
                          ₹
                          {primaryPricing.breakdown?.finalAmount?.toLocaleString(
                            "en-IN",
                            { maximumFractionDigits: 0 }
                          )}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Multiple Branches Indicator */}
                {product.pricing?.summary?.availableAtBranches?.length > 1 && (
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    Available at{" "}
                    {product.pricing.summary.availableAtBranches.length}{" "}
                    branches
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Product Details Modal */}
        <ProductDetailsModal
          product={product}
          isOpen={showDetails}
          onClose={() => setShowDetails(false)}
          onSelect={onSelect}
          isSelected={isSelected}
        />
      </>
    );
  }

  // Grid View
  return (
    <>
      <div
        className={`group relative bg-white rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden ${
          isSelected ? "ring-2 ring-primary shadow-lg" : ""
        }`}
        onClick={handleCardClick}
      >
        {/* Checkbox Overlay */}
        {showCheckbox && multiSelect && (
          <div
            className="absolute top-3 left-3 z-10"
            onClick={(e) => {
              e.stopPropagation();
              onSelect();
            }}
          >
            <div
              className={`w-6 h-6 rounded-lg border-2 transition-all shadow-sm ${
                isSelected
                  ? "bg-primary border-primary scale-110"
                  : "bg-white border-gray-300 group-hover:border-primary"
              }`}
            >
              {isSelected && (
                <CheckCircleIcon className="w-full h-full text-white" />
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="absolute top-3 right-3 z-10 flex gap-2">
          {/* View Details Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowDetails(true);
            }}
            className="action-button p-2 bg-white/90 backdrop-blur-sm rounded-lg hover:bg-white shadow-sm transition-all opacity-0 group-hover:opacity-100"
            title="View Details"
          >
            <EyeIcon className="w-4 h-4 text-gray-600" />
          </button>

          {/* Edit Button */}
          {onEdit && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              className="action-button p-2 bg-white/90 backdrop-blur-sm rounded-lg hover:bg-white shadow-sm transition-all opacity-0 group-hover:opacity-100"
              title="Edit Product"
            >
              <PencilIcon className="w-4 h-4 text-gray-600" />
            </button>
          )}
        </div>

        {/* Product Media */}
        <div className="relative h-56 bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden">
          {firstMedia ? (
            <>
              {isFirstVideo ? (
                <>
                  <video
                    src={firstMedia.url}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors">
                    <div className="bg-white/90 rounded-full p-4">
                      <PlayIcon className="w-8 h-8 text-gray-900" />
                    </div>
                  </div>
                </>
              ) : (
                <img
                  src={firstMedia.url}
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
              )}
            </>
          ) : (
            <div className="flex items-center justify-center h-full">
              <CubeIcon className="w-16 h-16 text-gray-400" />
            </div>
          )}

          {/* Media Count Badges */}
          {(imageCount > 0 || videoCount > 0) && (
            <div className="absolute bottom-3 left-3 flex gap-2">
              {imageCount > 0 && (
                <div className="bg-black/60 backdrop-blur-sm text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                  <PhotographIcon className="w-3 h-3" />
                  {imageCount}
                </div>
              )}
              {videoCount > 0 && (
                <div className="bg-primary/80 backdrop-blur-sm text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                  <VideoCameraIcon className="w-3 h-3" />
                  {videoCount}
                </div>
              )}
            </div>
          )}

          {/* Feature Badges */}
          <div className="absolute bottom-3 right-3 flex flex-wrap gap-2 justify-end">
            {product.hallmark?.isHallmarked && (
              <span className="inline-flex items-center px-2 py-1 bg-yellow-400 text-yellow-900 text-xs font-medium rounded-full shadow-sm">
                <ShieldCheckIcon className="w-3 h-3 mr-1" />
                Hallmarked
              </span>
            )}
            {product.isFeatured && (
              <span className="inline-flex px-2 py-1 bg-green-400 text-green-900 text-xs font-medium rounded-full shadow-sm">
                Featured
              </span>
            )}
          </div>

          {/* Stock Status Badge */}
          <div className="absolute top-3 left-3">
            <Badge
              variant={stockStatus.variant}
              size="sm"
              className="shadow-sm"
            >
              {stockStatus.label}
            </Badge>
          </div>
        </div>

        {/* Product Info */}
        <div className="p-4">
          <div className="mb-3">
            <h3 className="font-semibold text-gray-900 text-base line-clamp-2 min-h-[3rem]">
              {product.name}
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              {product.sku} • {product.productId}
            </p>
          </div>

          {/* Type Badges */}
          <div className="flex flex-wrap gap-2 mb-3">
            <Badge variant="secondary" size="sm">
              {product.type}
            </Badge>
            {product.subType && (
              <Badge variant="secondary" size="sm">
                {product.subType.replace("_", " ")}
              </Badge>
            )}
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-2 gap-3 text-sm mb-4">
            <div>
              <p className="text-xs text-gray-500">Gross Weight</p>
              <p className="font-semibold text-gray-900">
                {product.grossWeight}g
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Net Weight</p>
              <p className="font-semibold text-gray-900">
                {product.netWeight}g
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Purity</p>
              <p className="font-semibold text-gray-900">
                {product.metalDetails?.purity || product.purity}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Stock</p>
              <p className="font-semibold text-gray-900">
                {product.stock?.availableQuantity || 0}
              </p>
            </div>
          </div>

          {/* Pricing */}
          {showPricing && primaryPricing && (
            <div className="border-t border-gray-200 pt-4">
              <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-lg p-3">
                <p className="text-xs text-primary-700 font-medium mb-1">
                  Starting Price
                </p>
                <div className="flex items-baseline justify-between">
                  <span className="text-2xl font-bold text-primary">
                    ₹
                    {primaryPricing.breakdown?.finalAmount?.toLocaleString(
                      "en-IN",
                      { maximumFractionDigits: 0 }
                    )}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowPricingDetails(!showPricingDetails);
                    }}
                    className="pricing-toggle text-xs text-primary-700 hover:text-primary-900 font-medium"
                  >
                    {showPricingDetails ? "Hide" : "Details"}
                  </button>
                </div>

                {/* Compact Breakdown */}
                {showPricingDetails && (
                  <div className="mt-3 pt-3 border-t border-primary-200 space-y-1.5 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Metal:</span>
                      <span className="font-medium">
                        ₹
                        {primaryPricing.breakdown?.metalValue?.toLocaleString(
                          "en-IN",
                          { maximumFractionDigits: 0 }
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Making:</span>
                      <span className="font-medium">
                        ₹
                        {primaryPricing.breakdown?.makingCharges?.toLocaleString(
                          "en-IN",
                          { maximumFractionDigits: 0 }
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">GST:</span>
                      <span className="font-medium">
                        ₹
                        {primaryPricing.breakdown?.gst?.amount?.toLocaleString(
                          "en-IN",
                          { maximumFractionDigits: 0 }
                        )}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Branch Info */}
              {product.pricing?.summary?.availableAtBranches?.length > 1 && (
                <p className="text-xs text-gray-500 mt-2 text-center">
                  +{product.pricing.summary.availableAtBranches.length - 1} more
                  branch
                  {product.pricing.summary.availableAtBranches.length > 2
                    ? "es"
                    : ""}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Product Details Modal */}
      <ProductDetailsModal
        product={product}
        isOpen={showDetails}
        onClose={() => setShowDetails(false)}
        onSelect={onSelect}
        isSelected={isSelected}
      />
    </>
  );
};

export default EnhancedProductCard;
